import {
    RiAlertLine,
    RiCalendar2Line,
    RiCalendarCheckLine, RiCalendarEventLine, RiCheckDoubleLine, RiDownloadCloud2Line, RiExchangeDollarFill, RiExchangeDollarLine,
    RiFileList2Line,
    RiLoader2Line,
    RiMoneyDollarCircleLine, RiUserLine,
} from "@remixicon/react";
import classNames from "classnames";
import React, {
    ReactElement,
    ReactNode,
    useMemo,
} from "react";
import {
    PortalBillingPayment,
    PortalBillingProfile,
    PortalBillingSubscription,
    PortalPaymentStatus,
    useGetBillingPaymentsQuery,
} from "../../../../__generated__/api";
import DateRangeCalendarPicker from "../../../../components/pickers/date-range/DateRangeCalendarPicker";
import Badge from "../../../../components/theme/badge/Badge";
import Button from "../../../../components/theme/button/RouterLink";
import Icon from "../../../../components/theme/icon/Icon";
import SettingsContainer from "../../../../components/theme/settings";
import createTableContainer from "../../../../components/theme/table";
import Text from "../../../../components/theme/text/Text";
import useArticleContext from "../../../../contexts/HelpContext";
import { Paths } from "../../../../contexts/Routing";
import { parseFilterDates, tryParseDate } from "../../../../date";
import Styles from "./Billing.module.scss";
import { DURATION_TO_NAME, IMAGES } from "./BillingCheckout";
import TimedMessage from "../../../../components/timed-message/TimedMessage";
import { SuccessConfetti } from "../../../../components/confetti/Confetti";

interface PortalBillingPaymentWithId extends PortalBillingPayment {
    id: string;
}
const TableContainer = createTableContainer<PortalBillingPaymentWithId>();

function IconLabelValue({
    icon,
    label,
    value,
}: {
    icon: ReactNode;
    label: string;
    value: string | number | undefined;
}): ReactElement {
    return (
        <div className={Styles["subscription-label-value"]}>
            <Icon isRounded icon={icon} variant="primary" size="auto" background="primary" />
            <div>
                <Text as="div" textVariant="body-2-regular" colorVariant="body-secondary">
                    {label}
                </Text>
                <Text as="div" textVariant="body-2-medium" colorVariant="body-primary">
                    {value}
                </Text>
            </div>
        </div>
    );
}

const PAYMENT_STATUS_BADGE = {
    [PortalPaymentStatus.Succeeded]: (
        <Badge variant="success" prefixIcon={<RiCheckDoubleLine />}>
            Paid
        </Badge>
    ),
    [PortalPaymentStatus.Processing]: (
        <Badge variant="yield" prefixIcon={<RiLoader2Line />}>
            Pending
        </Badge>
    ),
    [PortalPaymentStatus.Error]: (
        <Badge variant="danger" prefixIcon={<RiAlertLine />}>
            Failed
        </Badge>
    ),
};

const PAYMENT_STATUS_NAMES = {
    [PortalPaymentStatus.Succeeded]: "paid",
    [PortalPaymentStatus.Processing]: "pending",
    [PortalPaymentStatus.Error]: "failed",
};

export function BillingSuccess(): ReactElement {
    return (
        <SuccessConfetti timeout={5000}>
            <TimedMessage
                shouldShow
                variant="success"
                title="🎉 Thanks for signing up!"
            >
                You can now unlock the power of the full Foqal platform.
            </TimedMessage>
        </SuccessConfetti>
    );
}

function BillingSubscriptionPlan({
    subscription,
    profile,
}: {
    subscription: PortalBillingSubscription;
    profile: PortalBillingProfile;
}): ReactNode {
    let tierIndex = profile.tiers?.findIndex(tier => tier.encryptedTierId === subscription.tier.encryptedTierId);
    if (!tierIndex || tierIndex <= 0) {
        tierIndex = 0;
    }

    const { price, tier } = subscription;

    const patternIndex = tierIndex % IMAGES.length;
    const Pattern = IMAGES[patternIndex];

    const durationName = DURATION_TO_NAME[price.duration];

    const formatter = new Intl.NumberFormat(undefined, {
        style: "currency",
        currency: price.currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
        currencyDisplay: "symbol",
    });

    return (

        <div className={Styles.subscription}>
            {tier && (
                <div
                    className={classNames(Styles.tier, Styles[`tier-${tierIndex + 1}`])}
                >
                    <Pattern className={classNames(Styles.pattern, Styles[`pattern-${patternIndex + 1}`])} />

                    <div className={Styles["tier-line"]}>
                        <Text as="h2" textVariant="subtitle-2">
                            {tier.name}
                        </Text>
                        {" "}
                        <Badge variant="primary">
                            Billed
                            {" "}
                            {durationName.text}
                        </Badge>
                    </div>

                    <div className={Styles["subscription-price"]}>
                        <Text
                            as="h2"
                            textVariant="title"
                            colorVariant="text-title"
                        >
                            {formatter.format((price.price * (subscription.seatCount ?? 1)) / 100)}
                        </Text>
                        <Text
                            as="span"
                            textVariant="body-2-regular"
                            colorVariant="body-secondary"
                        >
                            /
                        </Text>
                        <Text
                            as="span"
                            textVariant="body-2-regular"
                            colorVariant="body-secondary"
                        >
                            {" "}
                            {durationName.priceText}
                            <br />
                            for
                            {" "}
                            {subscription.seatCount ?? 1}
                            {" "}
                            agents
                        </Text>
                    </div>
                    <Button
                        variant="outline-gray"
                        size="medium"
                        to={Paths.ADMINISTRATOR_SETTING_ID}
                        params={{ setting: "billing-modify", id: subscription.encryptedSubscriptionId }}
                    >
                        Edit Plan
                    </Button>
                </div>
            )}
            <div className={Styles["subscription-details"]}>
                <IconLabelValue
                    icon={<RiUserLine />}
                    label="Number of Agents"
                    value={subscription.seatCount ?? 1}
                />
                <IconLabelValue
                    icon={<RiMoneyDollarCircleLine />}
                    label="Price per Agent"
                    value={formatter.format(subscription.price.price / 100)}
                />
                <IconLabelValue
                    icon={<RiExchangeDollarLine />}
                    label="Upcoming Payment"
                    value={!subscription.endDate ? tryParseDate(subscription.nextRenewalDate)?.toLocaleDateString(undefined, {
                        day: "numeric",
                        month: "long",
                        year: "numeric",
                    }) : "-"}
                />
                <IconLabelValue
                    icon={<RiCalendarEventLine />}
                    label="Start Date"
                    value={tryParseDate(subscription.startDate)?.toLocaleDateString(undefined, {
                        day: "numeric",
                        month: "long",
                        year: "numeric",
                    }) ?? "-"}
                />
                <IconLabelValue
                    icon={<RiCalendarCheckLine />}
                    label="End Date"
                    value={tryParseDate(subscription.endDate)?.toLocaleDateString(undefined, {
                        day: "numeric",
                        month: "long",
                        year: "numeric",
                    }) ?? "-"}
                />
            </div>
        </div>

    );
}

export default function BillingSubscription({
    subscription,
    profile,
}: {
    subscription: PortalBillingSubscription;
    profile: PortalBillingProfile;
}): ReactElement {
    useArticleContext("urn:fq:article:67d9c5ea6d6c4cbd8ec38dcd75459521");

    const { price } = subscription;

    const formatter = new Intl.NumberFormat(undefined, {
        style: "currency",
        currency: price.currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
        currencyDisplay: "symbol",
    });

    const fractionFormatter = new Intl.NumberFormat(undefined, {
        style: "currency",
        currency: price.currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
        currencyDisplay: "symbol",
    });

    const formatFraction = (amount: number) => (amount % 1
        ? fractionFormatter.format(amount)
        : formatter.format(amount));

    const { data, loading: isLoading } = useGetBillingPaymentsQuery();

    const payments = useMemo(() => data?.billingPayments?.payments.map(payment => ({
        ...payment,
        id: payment.date.toString(),
    })) ?? [], [data?.billingPayments]);

    return (
        <SettingsContainer navigation="none">
            <BillingSuccess />
            {profile.subscriptions?.map(current => (
                <SettingsContainer.Section
                    key={current.encryptedSubscriptionId}
                    title="Subscription"
                    shouldDisplayTitle={false}
                >
                    <SettingsContainer.Setting
                        title="Your subscription"
                        description="Your current subscription plan"
                        permissions={SettingsContainer.Setting.Permissions.Administrator}
                        isHideTitle
                    >
                        <BillingSubscriptionPlan
                            subscription={current}
                            profile={profile}
                        />
                    </SettingsContainer.Setting>
                </SettingsContainer.Section>

            ))}
            <SettingsContainer.Section
                title="Payments"
                helpText="This is a list of all completed payments with their status, a
                    link to the reciept, and the date of transaction. If you have any questions
                    about any transactions, please contact support."
            >
                <SettingsContainer.Setting
                    title="Payments"
                    description="The list of all completed payments, their status, and a link to the reciept."
                    permissions={SettingsContainer.Setting.Permissions.Administrator}
                    isHideTitle
                >
                    <TableContainer
                        data={payments as PortalBillingPaymentWithId[] ?? []}
                        isLoading={isLoading}
                        borderVariant="secondary"
                    >
                        <TableContainer.Empty>
                            <TableContainer.Empty.Default
                                text="There have been no payments made on your account.
                                Once a subscription is active and charges have been made, you will
                                see transactions here."
                                title="No Payments"
                                icon={<RiExchangeDollarFill />}
                            />
                        </TableContainer.Empty>
                        <TableContainer.TableControls>
                            <TableContainer.TableControls.Search
                                search={(query, item) => item.amount.toString().includes(query)
                                    || PAYMENT_STATUS_NAMES[item.status].includes(query)
                                    || tryParseDate(item.date)?.toLocaleString(undefined, {
                                        month: "long",
                                        day: "numeric",
                                        year: "numeric",
                                    }).toLocaleLowerCase().includes(query)}
                            />
                            <TableContainer.TableControls.Seperator />
                            <TableContainer.TableControls.Filters />
                            <TableContainer.TableControls.Filter
                                defaultValue={{}}
                                deserialize={TableContainer.TableControls.Filter.Serializers.deserializeDateRange}
                                serialize={TableContainer.TableControls.Filter.Serializers.serializeDateRange}
                                filterKey="created"
                                onFilter={(item, date) => {
                                    const itemDate = tryParseDate(item.date)?.getTime();
                                    return itemDate != null
                                        && (date.startDate ? date.startDate <= itemDate : true)
                                        && (date.endDate ? date.endDate >= itemDate : true);
                                }}
                            >
                                {(dateRange, setDates) => (
                                    <DateRangeCalendarPicker
                                        toggleText="Created"
                                        icon={<RiCalendar2Line />}
                                        value={{
                                            startDate: dateRange.startDate ? new Date(dateRange.startDate) : undefined,
                                            endDate: dateRange.endDate ? new Date(dateRange.endDate) : undefined,
                                        }}
                                        onChange={change => {
                                            const dates = parseFilterDates(change);
                                            setDates({
                                                startDate: dates.startDate,
                                                endDate: dates.endDate,
                                            });
                                        }}
                                    />
                                )}

                            </TableContainer.TableControls.Filter>

                            <TableContainer.TableControls.SortBy />
                        </TableContainer.TableControls>
                        <TableContainer.Table>
                            <TableContainer.TableValue
                                columnKey="payment"
                                title="Payment"
                            >
                                {item => (
                                    <>
                                        <Icon icon={<RiFileList2Line />} variant="default" size="small" />
                                        {" "}
                                        Payment-
                                        {tryParseDate(item.date)?.toLocaleDateString(undefined, {
                                            day: "2-digit",
                                            month: "2-digit",
                                            year: "numeric",
                                        }).replaceAll("/", "-")}
                                    </>
                                )}
                            </TableContainer.TableValue>
                            <TableContainer.TableValue
                                columnKey="status"
                                title="Status"
                                sortBy={item => item.status}
                            >
                                {item => PAYMENT_STATUS_BADGE[item.status]}
                            </TableContainer.TableValue>

                            <TableContainer.TableValue
                                columnKey="date"
                                title="Payment Date"
                                sortBy={item => tryParseDate(item.date)}
                            >
                                {item => (
                                    tryParseDate(item.date)?.toLocaleDateString(undefined, {
                                        day: "numeric",
                                        month: "long",
                                        year: "numeric",
                                    })
                                )}
                            </TableContainer.TableValue>

                            <TableContainer.TableValue
                                columnKey="amount"
                                title="Amount"
                                sortBy={item => item.amount}
                            >
                                {item => formatFraction(item.amount / 100)}
                            </TableContainer.TableValue>

                            <TableContainer.Action
                                icon={<RiDownloadCloud2Line />}
                                altText="Download Receipt"
                                getUrl={item => item.receiptUrl}
                                isNewTab
                                canClick={column => column.status === PortalPaymentStatus.Succeeded}
                            />

                        </TableContainer.Table>
                    </TableContainer>
                </SettingsContainer.Setting>

            </SettingsContainer.Section>
        </SettingsContainer>

    );
}
